import { alpha, Box, ButtonBase, styled, Accordion, AccordionSummary, AccordionDetails } from "@mui/material"
import { Paragraph, Span } from "components/Typography"
import { UserContext } from "contexts/UserContext"
import { useContext, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { filterSidebarByRole } from "utils/permissions"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { navigations } from "../layout-parts/navigation"

const NavItemButton = styled(ButtonBase)(({ theme, active }) => ({
	height: 44,
	width: "100%",
	borderRadius: 8,
	marginBottom: 4,
	padding: "0 18px",
	justifyContent: "flex-start",
	transition: "all 0.15s ease",
	...(active && {
		color: theme.palette.primary.main,
		backgroundColor: alpha(theme.palette.primary.main, 0.06)
	}),
	"&:hover": {
		backgroundColor: theme.palette.action.hover
	}
}))

const ListLabel = styled(Paragraph)(({ theme, compact }) => ({
	fontWeight: 700,
	fontSize: "12px",
	marginTop: "20px",
	marginLeft: "15px",
	marginBottom: "10px",
	textTransform: "uppercase",
	transition: "all 0.15s ease",
	color: theme.palette.text.secondary,
	...(compact && {
		opacity: 0,
		width: 0
	})
}))

const ExternalLink = styled("a")(({ theme }) => ({
	overflow: "hidden",
	whiteSpace: "pre",
	marginBottom: "8px",
	textDecoration: "none",
	color: theme.palette.text.primary
}))

const StyledText = styled(Span)(({ theme, compact, active }) => ({
	whiteSpace: "nowrap",
	paddingLeft: "0.8rem",
	transition: "all 0.15s ease",
	fontSize: "13px",
	fontWeight: 500,
	color: active ? theme.palette.primary.main : theme.palette.text.secondary,
	...(compact && {
		opacity: 0,
		width: 0
	})
}))

const BulletIcon = styled("div")(({ theme, active }) => ({
	width: 4,
	height: 4,
	marginLeft: "10px",
	marginRight: "8px",
	overflow: "hidden",
	borderRadius: "50%",
	background: active ? theme.palette.primary.main : theme.palette.text.disabled,
	boxShadow: active ? `0px 0px 0px 3px ${theme.palette.primary[200]}` : "none"
}))

const BadgeValue = styled(Box)(({ compact, theme }) => ({
	color: "white",
	fontSize: "12px",
	fontWeight: 500,
	padding: "1px 6px",
	overflow: "hidden",
	borderRadius: "300px",
	backgroundColor: theme.palette.primary.main,
	display: compact ? "none" : "unset"
}))

const iconStyle = (active) => ({
	fontSize: 18,
	marginRight: "4px",
	color: active ? "primary.main" : "text.secondary"
})

const MultiLevelMenu = ({ sidebarCompact }) => {
	const navigate = useNavigate()
	const { pathname } = useLocation()

	const { dataUser } = useContext(UserContext)
	const permissions = dataUser.permissions || []

	const [flattenedNavigations, setFlattenedNavigations] = useState([])
	const [loading, setLoading] = useState(true)

	const activeRoute = (path) => {
		const normalizedPathname = pathname?.toLowerCase()
		const normalizedPath = path?.toLowerCase()

		return normalizedPathname === normalizedPath || (normalizedPathname && normalizedPathname.startsWith(`${normalizedPath}/`))
	}

	const handleNavigation = (path) => navigate(path)

	const COMPACT = sidebarCompact ? 1 : 0

	useEffect(() => {
		const filteredSidebar = filterSidebarByRole(navigations, permissions)
		// Flatten the filtered sidebar data
		const flattenedData = flattenNavigation(filteredSidebar)
		setFlattenedNavigations(flattenedData)
		setLoading(false)
	}, [permissions, navigations])

	// Function to flatten the navigation structure
	const flattenNavigation = (data) => {
		const flattened = []
		data.forEach((item) => {
			flattened.push(item)
			if (item.children) {
				item.children.forEach((child) => {
					flattened.push(child)
				})
			}
		})
		return flattened
	}

	if (!loading) {
		return (
			<>
				{flattenedNavigations.map((item, index) => {
					if (item.type === "label") {
						// Find children for the label
						const children = flattenedNavigations.filter((child) => child.parent === item.label && item.hasChild)
						if (children.length > 0) {
							return (
								<Accordion
									key={index}
									TransitionProps={{ unmountOnExit: true }}
									defaultExpanded={true} // Adjust this as per your requirement
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										aria-controls={`panel${index}-content`}
										id={`panel${index}-header`}
									>
										<ListLabel compact={COMPACT}>{item.label}</ListLabel>
									</AccordionSummary>
									<AccordionDetails>
										{children.map((child, idx) => (
											<NavItemButton
												key={idx}
												className="navItem"
												active={activeRoute(child.path)}
												onClick={() => handleNavigation(child.path)}
											>
												{child.icon ? <child.icon sx={iconStyle(activeRoute(child.path))} /> : <BulletIcon active={activeRoute(child.path)} />}
												<StyledText
													compact={COMPACT}
													active={activeRoute(child.path)}
												>
													{child.name}
												</StyledText>
												<Box mx="auto" />
												{child.badge && <BadgeValue compact={COMPACT}>{child.badge.value}</BadgeValue>}
											</NavItemButton>
										))}
									</AccordionDetails>
								</Accordion>
							)
						} else {
							return (
								<Box key={index}>
									<ListLabel compact={COMPACT}>{item.label}</ListLabel>
								</Box>
							)
						}
					} else if (item.hasChild === false) {
						return (
							<Box key={index}>
								<NavItemButton
									key={item.name}
									className="navItem"
									active={activeRoute(item.path)}
									onClick={() => handleNavigation(item.path)}
								>
									{item.icon ? <item.icon sx={iconStyle(activeRoute(item.path))} /> : <BulletIcon active={activeRoute(item.path)} />}
									<StyledText
										compact={COMPACT}
										active={activeRoute(item.path)}
									>
										{item.name}
									</StyledText>
									<Box mx="auto" />
									{item.badge && <BadgeValue compact={COMPACT}>{item.badge.value}</BadgeValue>}
								</NavItemButton>
							</Box>
						)
					}
				})}
			</>
		)
	}

	return null
}

export default MultiLevelMenu
